'use client'

import { useState, type ReactNode } from 'react'
import { FlashIcon, GiftIcon, ShieldIcon } from '@repo/ui'
import CoinsIcon from 'src/app/assets/icons/ico-coins.svg'
import SyncIcon from 'src/app/assets/icons/ico-sync-alt.svg'
import PercentIcon from 'src/app/assets/icons/ico-percent.svg'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import type { MarbleGame } from 'src/types/domain/Game'
import { useGetCurrentLanguage } from 'src/store/game'
import { selectRoundOdds } from '../BirdieRoundCard/roundOdds'
import { SingleRtpTable } from '../RightSidebar/components/SingleRtpTable'
import { ErrorHandlingSection } from '../RightSidebar/components/rulesSections/ErrorHandlingSection'
import { PlacingABetSection } from '../RightSidebar/components/rulesSections/PlacingABetSection'
import { BoardHeadIcon } from './BoardHeadIcon'
import { BirdieBallDefs } from './GolfBall'
import { PuttGlyph } from './PuttGlyph'
import { RoundMark } from './RoundMark'
import { glyphsForKey } from './tileViewModel'
import './birdie-rules.scss'

type Props = Readonly<{
  game: MarbleGame
}>

/**
 * The six exact sequences the board offers, in the board's own canonical keys.
 * All-holed and all-missed are not among them: they are the same thing as the
 * three-in and none-in counts, and the board does not sell them twice.
 */
const EXACT_OPTIONS = ['HHM', 'HMH', 'MHH', 'HMM', 'MHM', 'MMH'] as const
/** The four counts, zero to three, as the board orders them. */
const COUNT_OPTIONS = ['IN0', 'IN1', 'IN2', 'IN3'] as const
/**
 * B36. Ordered rarest LAST: the pair is read together and fire is the scarcer of the two. No
 * percentage is printed — the owner's note (2026-09-17) is that the rules should say what a thing
 * IS, and a reader deciding whether to back frost does not need its weight to a decimal place.
 * The PRICE is printed (2026-09-21): it is what a player backs, it is fixed, and it is read off the
 * round's odds rather than typed here.
 */
const ROUND_OPTIONS = ['FROST', 'FIRE'] as const

/**
 * Birdie's Game Rules body (5.10) — a rendering INSIDE the existing rules panel,
 * not a second panel. `GameRulesSidebar` keys its sections per game type and
 * Birdie, being `bettingMode: chip-placement`, used to fall into the generic
 * chip-placement blocks: a marble list, the winner-mode heading, a settlement
 * card about finish lines. None of that describes three putts, so the panel gates
 * once on the game type and hands the body to this component. The panel header
 * and its back button, in `<RulesPanel>`, stay shared.
 *
 * Every block here is one of the panel's OWN (`.your-goal-hero`,
 * `.game-flow-timeline`, `.betting-options-section`, `.info-card`, and the
 * existing `<SingleRtpTable>` for the figures), so it inherits the panel's
 * spacing, type and card chrome and cannot drift from them. The only selectors
 * of its own are the family header row and the option-card grids — see
 * `birdie-rules.scss`. Balls are the board's `<PuttGlyph>`, the family icons the
 * board's `<BoardHeadIcon>`, and the gradients they paint with are declared
 * once by `<BirdieBallDefs>` (idempotent, so the board's own copy on the same
 * page changes nothing).
 *
 * Copy is the owner-approved English, verbatim. The panel's namespace is
 * `gameRules`; the option names are the `marbleColors` keys the board already
 * ships in every locale, read the way the panel reads its own marble names.
 *
 * After the four cards come two of the panel's own collapsible sections —
 * placing a bet and error handling — mounted from the same components the
 * shared panel mounts (5.10c, `rulesSections/`), not copied. They collapse
 * and open exactly as they do there: this body keeps its own
 * `activeSectionId`, one section open at a time, a tap on the open header
 * closing it. Birdie's flags into them: the chip-placement body with the
 * Birdie gate on (no side-bet box, no auto-bet box — the board has neither),
 * and the generic error-handling pair.
 */
export function BirdieGameRules({ game }: Props) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage, 'gameRules')

  // The panel's own rule, verbatim (`GameRulesSidebar`): the open section's
  // id, or null; tapping the open one closes it.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const handleSectionClick = (sectionId: string) => {
    setActiveSectionId(activeSectionId === sectionId ? null : sectionId)
  }

  // Same rule as the shared panel: the reference language needs no disclaimer.
  const showTranslationDisclaimer = currentLanguage !== 'en' && currentLanguage !== 'en-soc'

  // Frost and Fire are priced from fixed weights, so their prices are the same every round —
  // but they are read off THIS round's odds, never typed here, so a retune can never leave the
  // rules quoting a stale figure. No odds yet (no round open) → the cards simply carry no price.
  const odds = selectRoundOdds(game.races[0])
  const roundPrice = (key: (typeof ROUND_OPTIONS)[number]): string | undefined => {
    const id = game.marbles.find((m) => m.color.toUpperCase() === key)?.uuid
    const price = id === undefined ? undefined : odds?.multipliers[id]
    if (price === undefined) return undefined
    return Number.isInteger(price) ? String(price) : price.toFixed(2)
  }

  return (
    <div className="panel-game-rules">
      {showTranslationDisclaimer && (
        <div className="info-box margin-bottom ai-translation-disclaimer">
          <div className="ai-translation-disclaimer-content">
            <div className="ai-translation-disclaimer-text">
              ℹ️{' '}
              {t(
                'This translation was generated using AI and may contain errors. English is the reference language for all game rules and terms.'
              )}
            </div>
          </div>
        </div>
      )}
      <div className="birdie-rules">
        <BirdieBallDefs />

        <div className="your-goal-hero">
          <div className="your-goal-hero__icon">
            <BoardHeadIcon column="exact" />
          </div>
          <h3 className="your-goal-hero__title">{t('Your goal')}</h3>
          <p className="your-goal-hero__text">
            {t(
              'A golfer takes three putts from the same spot. Before the first one, call what will happen — the exact sequence, or simply how many drop.'
            )}
          </p>
          <div className="your-goal-hero__disclaimer">
            {t(
              'Every round is a new golfer, a new green and a new distance — and the prices move with them.'
            )}
          </div>
        </div>

        <div className="game-flow-timeline">
          <div className="flow-step flow-step--yellow">
            <div className="flow-step__number">1</div>
            <div className="flow-step__content">
              <h4>{t('Pick')}</h4>
              <p>
                {t(
                  'Place your stake on any option while betting is open. Each option shows its price for this round.'
                )}
              </p>
            </div>
          </div>
          <div className="flow-step flow-step--red">
            <div className="flow-step__number">2</div>
            <div className="flow-step__content">
              <h4>{t('Watch the three putts')}</h4>
              <p>
                {t(
                  'Betting closes, the golfer putts, and each ball lights up as it drops or misses.'
                )}
              </p>
            </div>
          </div>
          <div className="flow-step flow-step--green">
            <div className="flow-step__number">3</div>
            <div className="flow-step__content">
              <h4>{t('Get paid')}</h4>
              <p>
                {t('Every matching option pays your stake multiplied by the price you took it at.')}
              </p>
            </div>
          </div>
        </div>

        <div className="betting-options-section">
          <h3 className="betting-options-section__title">{t('Two ways to play')}</h3>
          <p className="betting-options-section__subtitle">
            {t(
              'Exact order calls the three putts in sequence and pays when all three match. Any order calls only how many drop.'
            )}
          </p>

          <div className="birdie-rules__family">
            <span className="birdie-rules__family-icon">
              <BoardHeadIcon column="exact" />
            </span>
            <span className="birdie-rules__family-name">{t('Exact order')}</span>
            <span className="birdie-rules__family-hint">
              {t('read left to right, putt one to three')}
            </span>
          </div>
          <div className="birdie-rules__options birdie-rules__options--exact">
            {EXACT_OPTIONS.map((key) => (
              <OptionCard key={key} name={getTranslatedMarbleColor(key, t)} optionKey={key} />
            ))}
          </div>

          <div className="birdie-rules__family">
            <span className="birdie-rules__family-icon">
              <BoardHeadIcon column="count" />
            </span>
            <span className="birdie-rules__family-name">{t('Any order')}</span>
            <span className="birdie-rules__family-hint">{t('how many of the three drop')}</span>
          </div>
          <div className="birdie-rules__options birdie-rules__options--count">
            {COUNT_OPTIONS.map((key) => (
              <OptionCard key={key} name={getTranslatedMarbleColor(key, t)} optionKey={key} />
            ))}
          </div>
        </div>

        <div className="betting-options-section">
          <h3 className="betting-options-section__title">{t('Frost and Fire')}</h3>
          <p className="betting-options-section__subtitle">
            {t('A side bet on the round itself, not on the three putts.')}
          </p>
          <div className="birdie-rules__options birdie-rules__options--round">
            {ROUND_OPTIONS.map((key) => (
              <div
                className={`bet-option birdie-rules__round birdie-rules__round--${key.toLowerCase()}`}
                key={key}
              >
                <div className="bet-option__img">
                  <RoundMark kind={key} size={36} />
                </div>
                <div className="bet-option__name">{getTranslatedMarbleColor(key, t)}</div>
                <div className="birdie-rules__round-line">
                  {key === 'FROST' ? t('The green freezes over.') : t('The green catches fire.')}
                </div>
                {roundPrice(key) !== undefined && (
                  <div className="birdie-rules__round-price">
                    {t('Pays {{price}}x', { price: roundPrice(key) })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="birdie-rules__round-note">
            {t(
              'Most rounds are plain golf, and then neither pays. Now and then the green freezes over or catches fire as the last ball rolls, and every chip on the right one pays.'
            )}
          </p>
        </div>

        <div className="betting-options-section">
          <h3 className="betting-options-section__title">{t('Auto bet')}</h3>
          <p className="betting-options-section__subtitle">
            {t('Back the same kind of price every round, without pressing anything.')}
          </p>
          <div className="game-flow-timeline birdie-rules__auto">
            <div className="flow-step flow-step--yellow">
              <div className="flow-step__number">
                <SyncIcon />
              </div>
              <div className="flow-step__content">
                <h4>{t('Choose a price band')}</h4>
                <p>
                  {t(
                    'Short, middle, long, far — or drag the slider to your own. Every option priced inside the band gets a stake.'
                  )}
                </p>
              </div>
            </div>
            <div className="flow-step flow-step--red">
              <div className="flow-step__number">2</div>
              <div className="flow-step__content">
                <h4>{t('Set your amount')}</h4>
                <p>
                  {t(
                    'Pick the chip each option gets, cap what one round may cost, and choose how many rounds to run. Frost and Fire can ride along.'
                  )}
                </p>
              </div>
            </div>
            <div className="flow-step flow-step--green">
              <div className="flow-step__number">3</div>
              <div className="flow-step__content">
                <h4>{t('Let it run')}</h4>
                <p>
                  {t(
                    'Chips land on the board as each round opens and go out when betting closes, exactly as if you had placed them yourself.'
                  )}
                </p>
              </div>
            </div>
          </div>
          <p className="birdie-rules__round-note">
            {t(
              'If auto bet sits a round out, it tells you why. It only ever backs the options you chose, placing a chip by hand switches it off, and you can set it up while a round is running.'
            )}
          </p>
        </div>

        <RulesCard
          accent="chance"
          icon={<PercentIcon />}
          text={t(
            'Every round draws a new golfer, a new spot and a new distance, and the card shows how likely the putt is to drop. When the putt is hard, holed options pay more; when it is easy, the misses do.'
          )}
          title={t('Make chance')}
        />
        <RulesCard
          accent="lightning"
          icon={<FlashIcon />}
          text={t(
            'Some rounds are lightning rounds. When betting closes, one or more options light up with a boosted price, marked with a bolt — and every stake already on them gets the boost.'
          )}
          title={t('Lightning rounds')}
        />
        <RulesCard
          accent="payouts"
          icon={<CoinsIcon />}
          table={<SingleRtpTable gameType={game.track.gameType} />}
          text={t(
            'Win, and you get your stake times the price you took it at. Prices lock the moment you place a chip — nothing that happens later can change them.'
          )}
          title={t('Payouts')}
        />
        {/* The platform's money disclosures, the same strings the shared panel shows every
            other game (so every locale already carries them): the RTP caveat under the
            payouts, and below the cards the features and the FAQ entries that are live on
            Birdie — free bets and boosters auto-apply on the board, the operator's payout
            cap applies to every bet, check-in and levels pay into the multiplier. Copied
            from `GameRulesSidebar`, not shared: the shared tree stays byte-identical. */}
        <div className="info-box margin-bottom">
          <strong>{t('Important Notes:')}</strong>{' '}
          {t(
            'RTP is theoretical, measured over a large number of rounds — not a guarantee for any single session.'
          )}{' '}
          {t(
            '*Effective RTP includes the average contribution from Lightning Rounds and Player Level boosters over many bets.'
          )}
        </div>
        <RulesCard
          accent="fair"
          icon={<ShieldIcon />}
          text={t(
            'Every result is locked in before betting opens and revealed once the round settles. Open any past round to see the proof, or check it yourself on the independent verifier.'
          )}
          title={t('Provably fair')}
        />

        <PlacingABetSection
          flags={{ isBirdie: true, isChipPlacement: true, isCrashGame: false }}
          game={game}
          onToggle={() => handleSectionClick('placing-a-bet')}
          open={activeSectionId === 'placing-a-bet'}
        />
        <div
          className={`section section-collapsible ${activeSectionId === 'game-features' ? 'open' : ''}`}
        >
          <div className="section-header" onClick={() => handleSectionClick('game-features')}>
            <h3>{t('Game Features')}</h3>
            <div className="section-header-icon">
              <AngleRightIcon />
            </div>
          </div>
          <div className="section-content">
            <div className="feature-cards-grid">
              <div className="feature-card feature-card--checkin">
                <div className="feature-card__icon">
                  <GiftIcon />
                </div>
                <div className="feature-card__content">
                  <h4>{t('Daily Check-in')}</h4>
                  <p>
                    {t(
                      'Check in daily for multiplier boosts and rewards that stack with other bonuses.'
                    )}
                  </p>
                </div>
              </div>
              <div className="feature-card feature-card--level">
                <div className="feature-card__icon">
                  <ShieldIcon />
                </div>
                <div className="feature-card__content">
                  <h4>{t('Player Levels')}</h4>
                  <p>{t('Earn XP by wagering to unlock permanent multiplier boosts.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`section section-collapsible ${activeSectionId === 'faq' ? 'open' : ''}`}
          id="faq"
        >
          <div className="section-header" onClick={() => handleSectionClick('faq')}>
            <h3>{t('FAQ')}</h3>
            <div className="section-header-icon">
              <AngleRightIcon />
            </div>
          </div>
          <div className="section-content">
            <div className="info-box margin-bottom">
              <h3>{t('What are Free Bets?')}</h3>
              <p>
                {t(
                  'Free bets are bonus funds that can be used to place bets without using your real balance. When you win with a free bet, you receive the winnings minus the original stake amount.'
                )}
              </p>
              <ul>
                <li>
                  {t(
                    'Free bets auto-apply to the first option you place a chip on. The option locks to the free-bet amount — no extra chips can be added on top.'
                  )}
                </li>
                <li>
                  {t(
                    'If you have multiple free bets, the next ones apply in order to the next options you place chips on.'
                  )}
                </li>
                <li>{t('Some free bets may only be valid for specific games')}</li>
              </ul>
            </div>
            <div className="info-box margin-bottom">
              <h3>{t('What are Multiplier Boosters?')}</h3>
              <p>
                {t(
                  'Multiplier boosters provide a percentage boost on top of your base multiplier. They can be earned through daily check-ins, promotions, or other rewards.'
                )}
              </p>
              <ul>
                <li>
                  {t(
                    'Boosters auto-apply to the option you place each chip on, oldest booster first. Each booster is consumed when the bet is placed.'
                  )}
                </li>
                <li>{t('Boosters stack with Lightning Rounds and Player Level boosts.')}</li>
              </ul>
            </div>
            <div className="info-box margin-bottom">
              <h3>{t('Is there a maximum payout per bet?')}</h3>
              <p>
                {t(
                  'Yes — each operator sets a maximum payout per bet. When the combined effect of your base multiplier and any active boosts (lightning rounds, multiplier boosters, streak bonuses, level boosts) would push your potential payout above this limit, the payout is capped at the operator’s maximum.'
                )}
              </p>
              <ul>
                <li>
                  {t('Your bet is still placed normally — the cap only limits the maximum win')}
                </li>
                <li>
                  {t(
                    'The capped amount is shown as the potential payout in your bet history, so what you see is what you can win'
                  )}
                </li>
                <li>
                  {t(
                    'The cap is set per operator and applies to all bets — including high-multiplier outcomes during lightning rounds'
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <ErrorHandlingSection
          flags={{
            isCoinFlipGame: false,
            isCrashGame: false,
            isDuckRiverGame: false,
            isFishtankGame: false,
            isIrlAltGame: false,
            isIrlGame: false,
            isSnowRunGame: false,
            isSocialBettingGame: false
          }}
          onToggle={() => handleSectionClick('error-handling')}
          open={activeSectionId === 'error-handling'}
        />
      </div>
    </div>
  )
}

/**
 * One option card: the panel's `.bet-option` recipe with the board's balls in
 * place of the image. The balls are decorative — the name is the label — and
 * they are the SAME drawing the board's tiles use (`glyphsForKey`), so a count
 * card shows the balls that go in and the none-in card shows three misses.
 */
function OptionCard({ optionKey, name }: Readonly<{ optionKey: string; name: string }>) {
  return (
    <div className="bet-option">
      <div className="bet-option__img">
        <span aria-hidden="true" className="birdie-rules__balls">
          {(glyphsForKey(optionKey) ?? []).map((mark, index) => (
            <PuttGlyph key={`${optionKey}-${String(index)}`} mark={mark} size={16} />
          ))}
        </span>
      </div>
      <div className="bet-option__name">{name}</div>
    </div>
  )
}

/**
 * The panel's `.info-card` — icon, title, one description. The text goes
 * straight into `.info-card-description` the way the panel's own cards put
 * theirs: a paragraph element inside it would take the panel's generic
 * paragraph rule (brighter, taller, padded) and stand out from the cards
 * beside it. A card that also carries a `table` takes the panel's own column
 * recipe for a card with a full-width block under its icon row
 * (`info-card-with-betting-modes`, the settlement card's): beside the icon the
 * content column is ~240 px, which folds the RTP table's three columns into
 * four-line cells; under it the table has the whole card. Its spacing inside
 * the card is the one rule `birdie-rules.scss` adds for it.
 */
function RulesCard({
  accent,
  icon,
  title,
  text,
  table
}: Readonly<{
  /** One hue per card — the treatment the shared panel gives its feature cards — so the four read
   *  as four things at a glance. The colours live in `birdie-rules.scss`. */
  accent: 'chance' | 'lightning' | 'payouts' | 'fair'
  icon: ReactNode
  title: string
  text: string
  table?: ReactNode
}>) {
  const card = `birdie-rules__card birdie-rules__card--${accent}`
  const body = (
    <>
      <div className="info-card-icon">{icon}</div>
      <div className="info-card-content">
        <h3 className="info-card-title">{title}</h3>
        <div className="info-card-description">{text}</div>
      </div>
    </>
  )
  if (!table) return <div className={`info-card ${card}`}>{body}</div>
  return (
    <div className={`info-card info-card-with-betting-modes ${card}`}>
      <div className="info-card-icon-top">{body}</div>
      {table}
    </div>
  )
}
